import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { BottomLiquidGlassEffect } from '~/components/BottomLiquidGlassEffect';
import { DeliveryTimeWarning } from '~/components/HeaderV3/components/DeliveryTimeWarning/DeliveryTimeWarning';
import { UI_BOTTOM_COMPONENTS } from '~/constants/variables';
import {
  useBottomNavigation,
  useBottomNavigationBackHandler,
} from '~/hooks/useBottomNavigation';
import { useCartVibration } from '~/hooks/useCartVibration';
import { useMounted } from '~/hooks/useMounted';
import { ClubMemberDot } from '~/modules/membership/ui/club/ClubEntryPoints';
import eventEmitter, { EventsName } from '~/services/Events';
import { cartStore } from '~/stores';
import { bottomNavigationStore } from '~/stores/BottomNavigationStore';

import styles from './BottomNavigationBarV2.module.scss';
import { NavigationBarDE } from './components/NavigationBarDE';
import {
  type NavItem,
  NavigationBarFabrique,
  type NavigationBarProps,
} from './components/NavigationBarFabrique';
import { ProductDEBottomBar } from './components/ProductDEBottomBar';
import { useCartButton } from './hooks/useCartButton';
import { useProductNavigation } from './hooks/useProductNavigation';

const BottomNavigationBarV2 = observer(
  ({ isInModal = false }: { isInModal?: boolean }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const mounted = useMounted();
    useBottomNavigation();

    const { product } = bottomNavigationStore;

    const {
      count,
      notifyMeButtonText,
      notifyMeIcon,
      notifyMeOnClick,
      limit,
      limitReachedText,
      currentItem,
      calculatedPrice,
      isMultibuyActivated,
      isMultibuy,
    } = useProductNavigation({ product });

    const { vibrateOnChange } = useCartVibration({
      isMultibuy,
      requiredQuantity: currentItem?.getLowestMultibuyLevel()?.quantity,
      currentCount: count,
    });

    const {
      isHide,
      text,
      isLoading,
      price,
      additionalText,
      onClick: onClickCartButton,
    } = useCartButton();

    const [isVisibleNavBar] = useState(true);
    const [addedSku, setAddedSku] = useState<Nullable<string>>(null);
    const cartButtonRef = useRef<HTMLElement>(null);

    if (addedSku && addedSku !== currentItem?.sku) {
      setAddedSku(null);
    }

    const handleBack = useBottomNavigationBackHandler();

    const handleIncrement = () => {
      const newCount = count + 1;
      vibrateOnChange(newCount, 'add');
      eventEmitter.emit(EventsName.ADD_ITEM_TO_CART, {
        type: 'add',
        count: newCount,
      });
    };

    const handleDecrement = () => {
      const newCount = count - 1;
      vibrateOnChange(newCount <= 0 ? 0 : newCount, 'remove');
      eventEmitter.emit(EventsName.ADD_ITEM_TO_CART, {
        type: 'remove',
        count: newCount,
      });
    };

    const handleResetFilters = () => {
      eventEmitter.emit(EventsName.APPLY_FILTERS);
    };

    const handleApplyFilters = () => {
      eventEmitter.emit(EventsName.APPLY_FILTERS);
    };

    const handleAddToCart = () => {
      setAddedSku(currentItem?.sku ?? null);
      vibrateOnChange(1, 'add');
      eventEmitter.emit(EventsName.ADD_ITEM_TO_CART, { type: 'add', count: 1 });
    };

    const navigationItems: NavItem[] = [
      {
        to: '/',
        icon: 'home',
        isActive: pathname === '/',
      },
      {
        to: '/shop',
        icon: 'nav-bar-search',
        isActive: ['/shop', '/search'].includes(pathname),
      },
      company.isFavsTabEnabled && {
        to: '/favs',
        icon: 'heart',
        isActive: pathname === '/favs',
      },
      company.match({ cerise: true, default: false }) && {
        to: '/contact-us',
        icon: 'phone',
        isActive: pathname === '/contact-us',
      },
      !company.hideCabinetRoutes && {
        to: '/cabinet',
        icon: 'user',
        isActive: pathname.startsWith('/cabinet'),
        adornment: <ClubMemberDot />,
      },
    ].filter(Boolean) as NavItem[];

    const getNavigationProps = (): NavigationBarProps => {
      // Gate the persisted cart count behind mount so the first client render
      // matches the SSR markup (empty cart → spacer). Reading it during render
      // would swap spacer↔CartButton and crash hydration (whole tree discarded).
      const cartCount = mounted ? cartStore.totalCartCount : 0;
      const cart: NavigationBarProps['cart'] = {
        count: cartCount,
        asLink: true,
        ref: cartButtonRef,
      };

      const notFound = product ? product.notFound : true;

      const getCartButtonText = (): string => {
        if (!currentItem) {
          return '';
        }
        if (limit) {
          if (company.externalCartUrl) {
            return t(company.externalCartLabelKey ?? 'buyOnOzon');
          }
          return t('addToCart');
        }
        return notifyMeButtonText;
      };

      const getCartHandleClick = () => {
        if (limit) {
          if (company.externalCartUrl) {
            const ozonLink = Object.values(currentItem?.properties ?? {}).find(
              (property) => property.code === 'ozon_link',
            )?.value;
            const ozonUrl = typeof ozonLink === 'string' ? ozonLink : undefined;
            window.open(ozonUrl ?? company.externalCartUrl, '_blank');
            return;
          }
          return handleAddToCart();
        }
        return notifyMeOnClick();
      };

      switch (bottomNavigationStore.state) {
        case 'product':
          return {
            variant: notFound ? 'backOnly' : count ? 'withCounter' : 'withCTA',
            backButton: { onClick: handleBack },
            cta: count
              ? null
              : {
                  text: getCartButtonText(),
                  onClick: getCartHandleClick,
                  icon: !currentItem ? null : limit ? null : notifyMeIcon,
                  isLoading: !currentItem || Boolean(product?.isOfferLoading),
                },
            counter: count
              ? {
                  value: count,
                  onIncrement: handleIncrement,
                  onDecrement: handleDecrement,
                  isDecrementDisabled: count <= 0,
                  limit,
                  limitReachedText,
                  openLimitTooltipOnMount:
                    !!addedSku && addedSku === currentItem?.sku,
                  classes: {
                    root: styles.counter,
                    buttonClassName: styles.counterButton,
                    disabledClassName: styles.disabled,
                    valueWrapperClassName: styles.valueWrapper,
                    valueClassName: styles.value,
                  },
                  calculatedPrice: (
                    <span
                      className={cn(
                        styles.calculatedPrice,
                        isMultibuy && isMultibuyActivated && styles.greenPrice,
                      )}
                    >
                      {calculatedPrice}
                    </span>
                  ),
                }
              : null,
            cart: cartCount ? cart : null,
          };
        case 'cart': {
          return {
            variant: 'withCTA',
            cta: isHide
              ? null
              : {
                  text,
                  subtitle: price || additionalText,
                  // Not "is this button enabled" — it selects the layout: the
                  // label and the price side by side on one row. A subtitle
                  // that is a sentence rather than a price has to stay stacked
                  // under the label, or the two collide (they do in Russian and
                  // Uzbek before they do in English).
                  isActive: !!price,
                  disabled: cartStore.isMaxItemsPerOrderExceeded,
                  onClick: onClickCartButton,
                  isLoading,
                },
            backButton: { onClick: handleBack },
          };
        }
        case 'withActions':
          return {
            variant: 'withActions' as const,
            actions: {
              primary: {
                text: t('checkout:confirm'),
                onClick: handleApplyFilters,
              },
              secondary: {
                text: t('cancel'),
                onClick: handleResetFilters,
              },
            },
          };
        case 'backOnly':
          return {
            variant: 'backOnly' as const,
            backButton: { onClick: handleBack },
          };
        case 'backWithCart':
          return {
            variant: 'backWithCart' as const,
            backButton: { onClick: handleBack },
            cart: cartCount ? cart : null,
          };
        case 'withBack':
          return {
            variant: 'withBack' as const,
            backButton: { onClick: handleBack },
            navigation: { items: navigationItems },
            cart: cartCount ? cart : null,
          };

        default:
          return {
            variant: 'default' as const,
            navigation: { items: navigationItems },
            cart: cartCount ? cart : null,
          };
      }
    };

    if (
      company.isIncludedIn([CompanyName.DubaiExpress]) &&
      bottomNavigationStore.state === 'product' &&
      product &&
      !product.notFound
    ) {
      return (
        <ProductDEBottomBar
          currentItem={currentItem}
          count={count}
          limit={limit}
          isLoading={!currentItem || Boolean(product.isOfferLoading)}
          notifyMeText={notifyMeButtonText}
          onBack={handleBack}
          onAdd={handleAddToCart}
          onNotify={notifyMeOnClick}
        />
      );
    }

    if (
      company.is(CompanyName.DubaiExpress) &&
      (bottomNavigationStore.state === 'default' ||
        bottomNavigationStore.state === 'withBack')
    ) {
      return isVisibleNavBar && !isInModal ? <NavigationBarDE /> : null;
    }

    const props = getNavigationProps();

    return isVisibleNavBar ? (
      <div
        className={cn(styles.container, isInModal && styles.containerInModal)}
        data-company={company.name}
        id={UI_BOTTOM_COMPONENTS.NAVBAR}
      >
        {props.variant === 'backOnly' ||
        props.variant === 'withActions' ? null : (
          <BottomLiquidGlassEffect />
        )}
        <div className={styles.innerContainer}>
          <NavigationBarFabrique {...props} />
        </div>
        <DeliveryTimeWarning buttonRef={cartButtonRef} />
      </div>
    ) : null;
  },
);

export default BottomNavigationBarV2;
